import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
 
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { object as YupObject, string as YupString } from 'yup'
import { actions } from '../../widgets/Wizard/wizard.slice'
import {
	selectIsCheckboxChecked,
	selectWizardFormValues,
} from '../../widgets/Wizard/wizard.selectors'
import InputMask from 'react-input-mask'
import { isValidEmail, isValidPhoneNumber } from '../../helpers/regex.utils'
import { selectCustomerPlans } from '../Customers/customers.selectors'
import classNames from 'classnames'
import { getFormattedPlanName } from '../Reports/reports.constants'
import {
	getCardVendorName,
	isValidCard,
	isValidCVV,
} from '../../helpers/validateCardAndCVV'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import TextField from '@/components/forms/text-field'
import SelectField from '@/components/forms/select-field'
import PhoneField from '@/components/forms/phone-field'

const { setFormValues, setIsCurrentPageValid, setIsCheckboxChecked } = actions
const MINIMUM_STRING_LENGTH = 3
const MINIMUM_STRING_LENGTH_FOR_MONTH = 4
const AMERICAN_EXPRESS = 'American_Express'
const DINERS_CLUB = 'Diners_Club'
const emptyString = ''
const ZERO_DAYS = 0

const AccountDetails = () => {
	const dispatch = useDispatch()
	const wizardForm = useSelector(selectWizardFormValues)
	const planOptions = useSelector(selectCustomerPlans)
	const isCheckboxChecked = useSelector(selectIsCheckboxChecked)
	const [cardName, setCardName] = useState(emptyString)

	const isValidExpiryMonth = (expiryMonth, cardExpiryYear) => {
		const today = new Date()
		const currentMonth = today.getMonth() + 1
		const currentYear = today.getFullYear()
		const formattedCardExpiryYear =
			cardExpiryYear.length === 2
				? parseInt(currentYear.toString().slice(0, 2) + cardExpiryYear)
				: parseInt(cardExpiryYear)
		const isMonthValid =
			formattedCardExpiryYear === currentYear
				? parseInt(expiryMonth) >= currentMonth
				: true
		return parseInt(expiryMonth) > 0 && isMonthValid
	}

	const isValidExpiryYear = (expiryYear) => {
		const today = new Date()
		const currentYear = today.getFullYear()
		if (expiryYear.toString().length === MINIMUM_STRING_LENGTH_FOR_MONTH) {
			if (expiryYear < currentYear) return false
			else return true
		} else return false
	}

	const validationSchema = YupObject().shape({
		firstName: YupString()
			.min(MINIMUM_STRING_LENGTH, 'First Name should be at least 3 characters')
			.required('First Name is required'),
		lastName: YupString()
			.min(MINIMUM_STRING_LENGTH, 'Last Name should be at least 3 characters')
			.required('Last Name is required'),
		position: YupString().required('Position/Title is required'),
		phoneNumber: YupString()
			.trim()
			.length(10, 'Phone number should be at least 10 characters')
			.required('Phone number is required')
			.test(
				'format',
				'Enter valid phone number',
				(val) => val && isValidPhoneNumber(val)
			),
		desiredPlan: YupString().required('Plan is required'),
		cardNumber: YupString()
			.required('Card Number is required')
			.test(
				'card-validator',
				'Enter Valid Card Number',
				(val) => val && isValidCard(val) && getCardVendorName(val)
			),
		email: YupString()
			.email('E-mail is Invalid')
			.required('E-mail is required')
			.test('email', 'E-mail is Invalid', (val) => isValidEmail(val)),
		cardSecurityCode: YupString()
			.required('Security Code is required')
			.test('validate-cvv', 'Enter Valid CVV', function (val) {
				return isValidCVV(this.parent.cardNumber, val)
			}),
		cardExpiryMonth: YupString()
			.required('Card Expiry Month is required')
			.test('len', 'Card Expiry Month is out of Date.', function (val) {
				const formattedExpiryYear =
					(this && this.parent.cardExpiryYear) || emptyString
				return isValidExpiryMonth(val, formattedExpiryYear)
			}),
		cardExpiryYear: YupString()
			.required('Card Expiry Year is required')
			.test(
				'len',
				'Card Expiry Year is out of Date',
				(val) => val >= parseInt(new Date().getFullYear().toString().slice(2))
			),
	})

	const {
		accountFirstName,
		accountLastName,
		accountPosition,
		accountEmail,
		accountPhoneNumber,
		accountPlanId,
		cardNumber,
		cardSecurityCode,
		cardExpiryMonth,
		cardExpiryYear,
	} = wizardForm

	const form = useForm({
		resolver: yupResolver(validationSchema),
		defaultValues: {
			firstName: accountFirstName || emptyString,
			lastName: accountLastName || emptyString,
			position: accountPosition || emptyString,
			phoneNumber: accountPhoneNumber || emptyString,
			email: accountEmail || emptyString,
			desiredPlan: accountPlanId || emptyString,
			cardNumber: cardNumber || emptyString,
			cardSecurityCode: cardSecurityCode || emptyString,
			cardExpiryMonth: cardExpiryMonth || emptyString,
			cardExpiryYear: cardExpiryYear || emptyString,
		},
	})

	// Sync form changes to Redux wizard form
	useEffect(() => {
		const subscription = form.watch((values, { name }) => {
			if (!name) return
			switch (name) {
				case 'firstName':
					dispatch(setFormValues({ accountFirstName: values.firstName || emptyString }))
					break
				case 'lastName':
					dispatch(setFormValues({ accountLastName: values.lastName || emptyString }))
					break
				case 'position':
					dispatch(setFormValues({ accountPosition: values.position || emptyString }))
					break
				case 'phoneNumber':
					dispatch(setFormValues({ accountPhoneNumber: values.phoneNumber || emptyString }))
					break
				case 'email':
					dispatch(setFormValues({ accountEmail: values.email || emptyString }))
					break
				case 'desiredPlan':
					dispatch(setFormValues({ accountPlanId: values.desiredPlan || emptyString }))
					break
				case 'cardNumber':
					dispatch(setFormValues({ cardNumber: values.cardNumber || emptyString }))
					break
				case 'cardSecurityCode':
					dispatch(setFormValues({ cardSecurityCode: values.cardSecurityCode || emptyString }))
					break
				case 'cardExpiryMonth':
					dispatch(setFormValues({ cardExpiryMonth: values.cardExpiryMonth ? parseInt(values.cardExpiryMonth) : ZERO_DAYS }))
					break
				case 'cardExpiryYear':
					dispatch(setFormValues({ cardExpiryYear: values.cardExpiryYear || emptyString }))
					break
				default:
					break
			}
		})
		return () => subscription.unsubscribe()
	}, [dispatch, form])

	// Derived validations
	const checkIsCardValid = () => {
		const v = form.getValues()
		return (
			v.desiredPlan &&
			v.cardNumber &&
			isValidCard(v.cardNumber) &&
			cardName &&
			v.cardSecurityCode &&
			isValidCVV(v.cardNumber, v.cardSecurityCode) &&
			v.cardExpiryMonth &&
			v.cardExpiryYear &&
			isValidExpiryMonth(v.cardExpiryMonth, v.cardExpiryYear) &&
			isValidExpiryYear(v.cardExpiryYear)
		)
	}

	const isCardValid = isCheckboxChecked || checkIsCardValid()

	useEffect(() => {
		const isCardOk = isCheckboxChecked || checkIsCardValid()
		const validate = (values) => {
			if (
				values.firstName &&
				values.firstName.length >= MINIMUM_STRING_LENGTH &&
				values.lastName &&
				values.lastName.length >= MINIMUM_STRING_LENGTH &&
				values.position &&
				values.email &&
				isValidEmail(values.email) &&
				values.phoneNumber &&
				isValidPhoneNumber(values.phoneNumber) &&
				isCardOk
			) {
				dispatch(setIsCurrentPageValid(true))
			} else dispatch(setIsCurrentPageValid(false))
		}

		validate(form.getValues())
		const sub = form.watch((values) => validate(values))
		return () => sub.unsubscribe()
	}, [dispatch, form, isCheckboxChecked, cardName])

	useEffect(() => {
		const v = form.getValues()
		if (v.cardNumber) setCardName(getCardVendorName(v.cardNumber))
	}, [])

	const getFormattedExpiryMonth = (value) =>
		value ? (value < 10 ? '0' + parseInt(value) : value) : emptyString

	const getFormattedExpiryYear = (value) => {
		value = value.toString()
		return value.length > 2 ? value.slice(2) : value
	}

	const handleCheckbox = (isChecked) => {
		dispatch(setIsCheckboxChecked(isChecked))
		if (isChecked) {
			form.setValue('desiredPlan', emptyString)
			form.setValue('cardNumber', emptyString)
			form.setValue('cardSecurityCode', emptyString)
			form.setValue('cardExpiryMonth', emptyString)
			form.setValue('cardExpiryYear', emptyString)
			dispatch(
				setFormValues({
					accountPlanId: emptyString,
					cardNumber: emptyString,
					cardSecurityCode: emptyString,
					cardExpiryMonth: ZERO_DAYS,
					cardExpiryYear: ZERO_DAYS,
				})
			)
		}
	}

	const inputMaskClass = 'w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50'

	return (
		<Form {...form}>
			<div className='modal-content border-0'>
				<div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
					<TextField label='First Name' name='firstName' placeholder='First Name' />
					<TextField label='Last Name' name='lastName' placeholder='Last Name' className='md:mt-0' />

					<TextField label='Position / Title' name='position' placeholder='Title' />
					<PhoneField label='Phone Number' name='phoneNumber' />
					<div className='md:col-span-2'>
					<TextField label='Email Address' name='email' placeholder='user@gmail.com' />
					</div>
					<div className='md:col-span-2'>
						<div className='flex items-center gap-2'>
							<Switch id='external-customer' checked={isCheckboxChecked} onCheckedChange={(checked) => handleCheckbox(checked)} />
							<Label htmlFor='external-customer'>External, Integration Customer (No Credit Card Required)</Label>
						</div>
					</div>
					<SelectField
						label='Desired Plan'
						name='desiredPlan'
						placeholder='Select Plan'
						disabled={isCheckboxChecked}
						options={(planOptions || []).map(({ id, name }) => ({ value: String(id), label: getFormattedPlanName(name) }))}
					/>



					<FormField
						control={form.control}
						name='cardNumber'
						render={({ field }) => (
							<FormItem>
								<FormLabel className='f-14'>Card Number</FormLabel>
								<FormControl>
									<InputMask
										{...field}
										mask={cardName === DINERS_CLUB ? '9999 9999 9999 99' : cardName === AMERICAN_EXPRESS ? '9999 999999 99999' : '9999 9999 9999 9999'}
										disableunderline='true'
										maskChar={null}
										placeholder='**** **** **** ****'
										onChange={(ev) => {
											setCardName(getCardVendorName(ev.target.value))
											const value = ev.target.value.replace(/[^a-zA-Z0-9]/g, emptyString)
											field.onChange(value)
										}}
										className={inputMaskClass}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name='cardSecurityCode'
						render={({ field }) => (
							<FormItem>
								<FormLabel className='f-14'>Security Code</FormLabel>
								<FormControl>
									<InputMask
										{...field}
										mask={cardName === AMERICAN_EXPRESS ? '9999' : '999'}
										disableunderline='true'
										maskChar={null}
										placeholder={cardName === AMERICAN_EXPRESS ? '****' : '***'}
										onChange={(ev) => {
											const value = ev.target.value.replace(/[^a-zA-Z0-9]/g, emptyString)
											field.onChange(value)
										}}
										className={inputMaskClass}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<div className='space-y-2'>
						<Label className='f-14'>Expiration Date</Label>
						<div className='flex items-start gap-2'>
							<div className='relative flex-1'>
								<FormField
									control={form.control}
									name='cardExpiryMonth'
									render={({ field }) => (
										<FormItem>
											<FormControl>
												<InputMask
													{...field}
													placeholder='Month'
													maxLength={3}
													disableunderline='true'
													maskChar={null}
													onChange={(ev) => {
														const raw = ev.target.value
														if (parseInt(raw) < 13) {
															field.onChange(raw)
														} else {
															field.onChange(raw.slice(0, 2))
														}
													}}
													className={inputMaskClass}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
							<span className='mx-2 self-center'>/</span>
							<div className='relative flex-1'>
								<FormField
									control={form.control}
									name='cardExpiryYear'
									render={({ field }) => (
										<FormItem>
											<FormControl>
												<InputMask
													{...field}
													mask='99'
													disableunderline='true'
													maskChar={null}
													placeholder='Year'
													onChange={(ev) => {
														field.onChange(ev.target.value)
														const fullYear = parseInt(
															new Date()
															.getFullYear()
															.toString()
															.slice(0, 2)
															.concat(ev.target.value)
														)
														dispatch(setFormValues({ cardExpiryYear: fullYear }))
													}}
													className={inputMaskClass}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
		</Form>
	)
}

export default AccountDetails
